import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIsInDraftMode, useDraftModeToggle } from './useDraftMode';

/**
 * Conversations live in branch-scoped collections (AiThread resolves its
 * collection from Context.branch), so a thread that ran in Production is
 * invisible while the app sits in Develop mode and vice versa. Opening one
 * therefore has to align the app's mode with the thread's branch first.
 */

/** Ruthie threads have their own branch, selected by the /ruthie path itself. */
export function conversationPath(threadId: string, agentType?: string | null): string {
  return agentType === 'data_architect'
    ? `/ruthie/conversation/${threadId}`
    : `/ellie/conversation/${threadId}`;
}

/**
 * True when the app's current mode would look in the wrong collection for this
 * thread. RUTHIE and unknown branches never trigger a switch — the former is
 * driven by the route, the latter we can't reason about.
 */
export function needsModeSwitch(
  threadBranch: string | null | undefined,
  isInDraftMode: boolean,
): boolean {
  const branch = (threadBranch || '').toUpperCase();
  if (branch === 'DRAFT') return !isInDraftMode;
  if (branch === 'PROD') return isInDraftMode;
  return false;
}

export interface PendingConversation {
  threadId: string;
  path: string;
  /** Branch the conversation happened in. */
  branch: string;
  /** Mode we'd be switching into. */
  targetMode: 'develop' | 'production';
}

/**
 * Opens a conversation, asking first if doing so means switching modes.
 *
 * Pair with <ConversationModeSwitchDialog> and render it alongside; `pending`
 * drives the dialog, `confirm` switches then navigates, `cancel` bails.
 */
export function useOpenConversation() {
  const navigate = useNavigate();
  const isInDraftMode = useIsInDraftMode();
  const toggleDraftMode = useDraftModeToggle();
  const [pending, setPending] = useState<PendingConversation | null>(null);
  const [switching, setSwitching] = useState(false);

  const open = (
    threadId: string,
    { branch, agentType }: { branch?: string | null; agentType?: string | null } = {},
  ) => {
    const path = conversationPath(threadId, agentType);
    if (needsModeSwitch(branch, isInDraftMode)) {
      setPending({
        threadId,
        path,
        branch: (branch || '').toUpperCase(),
        targetMode: isInDraftMode ? 'production' : 'develop',
      });
      return;
    }
    navigate(path);
  };

  const confirm = async () => {
    if (!pending) return;
    setSwitching(true);
    try {
      // Flips the mode and invalidates every query, so the conversation page
      // mounts already reading the right branch.
      await toggleDraftMode('open-conversation');
      navigate(pending.path);
      setPending(null);
    } finally {
      setSwitching(false);
    }
  };

  const cancel = () => setPending(null);

  return { open, pending, confirm, cancel, switching };
}
